// ═══════════════════════════════════════════════════════════════
// Coming back from the bank
// ═══════════════════════════════════════════════════════════════
// An OAuth bank does not hand control back to a modal — it redirects the
// whole browser here, to /plaid-oauth, with its state on the query string.
// MOS has been unloaded and re-booted in the meantime, so this screen picks
// up the link token stashed on the way out and re-opens Plaid Link with the
// URL we landed on. Link reads its own state out of that URL, finishes the
// handshake, and fires onSuccess exactly as it would have in the modal.
//
// This mounts INSIDE the authenticated app on purpose. The exchange is a
// tier-3 route and bearerHeader() only carries a real token once AuthContext
// has hydrated the session; short-circuiting before that would 401.
//
// Every ending returns the person to the Accounting board rather than leaving
// them parked on a URL that means nothing to them.

import { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  loadPlaidLink, readLinkHandoff, clearLinkHandoff, exchangePublicToken,
} from '../../utils/plaidLink';
import { LAST_LOCATION_KEY } from '../../utils/lastLocation';

type Phase = 'working' | 'done' | 'failed';

/** Land back on the Money Desk, not on a blank root. Writing the location
 *  snapshot is how MOS restores a place across a reload. */
function returnToAccounting(): void {
  try {
    const snapshot = JSON.stringify({ view: 'ar-workbench' });
    sessionStorage.setItem(LAST_LOCATION_KEY, snapshot);
    localStorage.setItem(LAST_LOCATION_KEY, snapshot);
  } catch { /* storage disabled — the app still boots, just at its default */ }
  window.location.replace('/');
}

export function PlaidOAuthReturn() {
  const [phase, setPhase] = useState<Phase>('working');
  const [message, setMessage] = useState('Finishing the connection with your bank…');
  const started = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev; opening Link twice would
    // fight itself over the same one-time OAuth state.
    if (started.current) return;
    started.current = true;

    const handoff = readLinkHandoff();
    if (!handoff) {
      setPhase('failed');
      setMessage(
        'This connection took too long, so the bank sent you back after the session had expired. '
        + 'Nothing was connected and nothing was lost — start it again from the Money Desk.',
      );
      return;
    }

    void (async () => {
      try {
        await loadPlaidLink();
        if (!window.Plaid) throw new Error('Plaid window failed to load');

        window.Plaid.create({
          token: handoff.linkToken,
          receivedRedirectUri: window.location.href,
          onSuccess: (publicToken, metadata) => {
            void (async () => {
              try {
                if (handoff.itemId) {
                  clearLinkHandoff();
                  setPhase('done');
                  setMessage('Reconnected. The next sync picks it back up.');
                } else {
                  const r = await exchangePublicToken(
                    publicToken, metadata?.institution?.name ?? '');
                  clearLinkHandoff();
                  setPhase('done');
                  setMessage(
                    `Connected ${r.institution} — ${r.accounts} account${r.accounts === 1 ? '' : 's'}. `
                    + 'Map each one to its QuickBooks account on the Money Desk.',
                  );
                }
                setTimeout(returnToAccounting, 2200);
              } catch (e) {
                // The bank said yes; we failed to record it. Say which, because
                // the fix is different: this one is safe to simply retry.
                setPhase('failed');
                setMessage(
                  `Your bank approved the connection but MOS could not record it: `
                  + `${e instanceof Error ? e.message : 'unknown error'}. Try connecting again.`,
                );
              }
            })();
          },
          onExit: (err) => {
            clearLinkHandoff();
            setPhase('failed');
            setMessage(
              err
                ? 'The bank did not complete the connection. Nothing was changed — try again from the Money Desk.'
                : 'Connection cancelled. Nothing was changed.',
            );
          },
        }).open();
      } catch (e) {
        setPhase('failed');
        setMessage(e instanceof Error ? e.message : 'Could not reopen the connection');
      }
    })();
  }, []);

  const Icon = phase === 'working' ? Loader2 : phase === 'done' ? CheckCircle2 : AlertCircle;
  const tone = phase === 'working'
    ? 'text-slate-500'
    : phase === 'done' ? 'text-emerald-600' : 'text-amber-600';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="max-w-md w-full rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <Icon className={`mx-auto h-8 w-8 ${tone} ${phase === 'working' ? 'animate-spin' : ''}`} />
        <h1 className="mt-4 text-lg font-semibold text-slate-900">
          {phase === 'working' ? 'Connecting your bank' : phase === 'done' ? 'Bank connected' : 'Not connected'}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{message}</p>
        {phase !== 'working' && (
          <button
            type="button"
            onClick={returnToAccounting}
            className="mt-6 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Back to the Money Desk
          </button>
        )}
      </div>
    </div>
  );
}
